import React from 'react';
import { ClerkProvider, useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useDispatch } from 'react-redux';
import { notifyIsPDP } from 'state/app';
import { notifyAuthSuccess } from 'state/auth';

const AuthContextProvider = ({ children }) => {
    // 1. Fetch config from env
    const publishableKey =
        process.env.REACT_APP_CML_INSIGHTS_CLERK_PUBLISHABLE_KEY;

    // Satellite Configuration
    const domain = process.env.REACT_APP_CML_INSIGHTS_CLERK_DOMAIN; // e.g., 'satellite.example.com' or 'localhost:3000'
    const isSatellite = process.env.REACT_APP_CML_INSIGHTS_CLERK_IS_SATELLITE === 'true';
    const signInUrl = process.env.REACT_APP_CML_INSIGHTS_CLERK_SIGN_IN_URL; // Required for satellites

    // Safety check for keys
    if (!publishableKey) {
        // eslint-disable-next-line no-console
        console.error('[Auth] Clerk Publishable Key is missing! Check your .env file.');
    }

    return (
        <ClerkProvider
            publishableKey={publishableKey}
            domain={domain}
            isSatellite={isSatellite}
            signInUrl={signInUrl}
            localization={{
                signIn: {
                    start: {
                        title: 'Welcome Back',
                        subtitle: 'Please sign in to access the platform administration center.',
                        actionText: "Don't have an account?",
                        actionLink: 'Please contact your administrator.',
                    },
                },
                formFieldLabel__emailAddress: 'Email Address',
                formFieldLabel__password: 'Password',
            }}
            appearance={{
                layout: {
                    socialButtonsVariant: 'iconButton',
                    logoPlacement: 'inside',
                    showOptionalFields: false,
                },
                variables: {
                    colorPrimary: '#0069da',
                    colorText: '#1e293b',
                    colorTextSecondary: '#64748b',
                    fontFamily: 'Roboto, sans-serif',
                },
                elements: {
                    card: {
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        borderRadius: '16px',
                        border: '1px solid #f1f5f9',
                        padding: '40px',
                    },
                    headerTitle: {
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#0f172a',
                    },
                    headerSubtitle: {
                        fontSize: '14px',
                        color: '#64748b',
                        marginBottom: '24px',
                    },
                    formFieldLabel: {
                        fontWeight: '600',
                        color: '#334155',
                        fontSize: '13px',
                    },
                    formFieldInput: {
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                    },
                    formButtonPrimary: {
                        fontSize: '15px',
                        fontWeight: '600',
                        textTransform: 'none',
                        backgroundColor: '#0069da',
                        height: '44px',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#0056b3',
                        },
                    },
                    footer: {
                        '& .cl-internal-ph6id7': { // This is a hacky way to hide Clerk branding if needed, but better use official layout
                            display: 'none',
                        }
                    },
                    footerActionText: {
                        color: '#64748b',
                        fontSize: '13px',
                    },
                    footerActionLink: {
                        color: '#0069da',
                        fontWeight: '600',
                        fontSize: '13px',
                        '&:hover': {
                            color: '#0056b3',
                        }
                    }
                },
            }}
        >
            {children}
        </ClerkProvider>
    );
};

const useAuth = () => {
    const dispatch = useDispatch();
    const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
    const { user } = useUser();
    const clerk = useClerk();

    React.useEffect(() => {
        const syncAuth = async () => {
            if (isLoaded && isSignedIn && user) {
                try {
                    const token = await getToken();
                    if (token) {
                        localStorage.setItem('CML_INSIGHTS_USER_ACCESS_TOKEN', token);
                    }

                    const { publicMetadata } = user;
                    const cmlUserId = publicMetadata?.user_id;
                    const cmlOrgId = publicMetadata?.organization_id;
                    const roles = publicMetadata?.roles || [];
                    const isPDP = publicMetadata?.is_pdp || false;

                    if (cmlUserId) localStorage.setItem('CML_INSIGHTS_USER_ID', cmlUserId);
                    if (cmlOrgId) localStorage.setItem('CML_INSIGHTS_ORGANIZATION_ID', cmlOrgId);

                    dispatch(notifyIsPDP(isPDP));

                    const profile = {
                        id: user.id,
                        name: user.fullName || user.firstName || user.username,
                        email: user.primaryEmailAddress?.emailAddress ?? undefined,
                        preferred_username: user.username,
                    };

                    dispatch(notifyAuthSuccess({ authenticated: true, profile, roles }));
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('[Auth] Error syncing auth state:', error);
                }
            }
        };

        syncAuth();
    }, [isLoaded, isSignedIn, user, dispatch, getToken]);

    return {
        initialized: isLoaded,
        isSignedIn,
        user,
        getToken,
        signOut,
        clerk,
    };
};

export { AuthContextProvider, useAuth };
